import React from 'react';
import { Box, styled } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import { AvHighlight } from '../AvHighlight';
import type { AvKeyValueProps } from './AvKeyValue';

const MIN_ROW_HEIGHT = 27;

const StyledContainer = styled(Box)(({ theme }) => ({
  lineHeight: `${MIN_ROW_HEIGHT}px`,
  fontSize: '13px',
  margin: 0,
  position: 'relative',
  height: 'var(--total-size)',
  width: '100%',

  '& dl': {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    transform: 'var(--translate-y)',
  },

  '& .AvKeyValueV2__row-container': {
    position: 'relative',
    minHeight: `${MIN_ROW_HEIGHT}px`,
    borderRadius: '8px',
    paddingTop: '2px',
    paddingBottom: '2px',
    paddingLeft: '2px',
    width: '100%',
    transition: theme.transitions.create(['opacity', 'backgroundColor'], { duration: theme.transitions.duration.short }),
    ':hover': { backgroundColor: 'var(--color-surface-table-row-hover)' },
    ':hover .showInlineAction ': { opacity: 1 },
  },

  '& .AvKeyValueV2__row-container .showInlineAction': {
    position: 'absolute',
    paddingLeft: '32px',
    paddingRight: '16px',
    right: 0,
    top: 0,
    zIndex: 1,
    opacity: 0,
    gap: '16px',
    backgroundImage: `linear-gradient(to right, transparent, var(--color-surface-table-row-hover) 32px)`,
    // Intended
    // eslint-disable-next-line fabric-ui/css-best-practices
    display: 'flex',
    alignItems: 'center',
    height: '100%',
  },

  dt: {
    backgroundColor: 'var(--color-surface-interactive-modifier-default)',
    padding: '2px 6px',
    borderRadius: '8px',
    fontWeight: 500,
    '&:after': { content: `':'` },
    '+ dd': { marginLeft: '4px', wordBreak: 'break-all' },
  },
  'dt, dd': { display: 'inline' },
}));

const Row = ({
  k,
  v,
  highlightText,
  inlineActions,
  ...props
}: { k: string; v: string } & Pick<AvKeyValueProps, 'highlightText' | 'inlineActions'> & React.ComponentProps<'div'>) => {
  return (
    <div className="AvKeyValueV2__row-container" {...props}>
      <dt>{k}</dt>
      <dd>
        <AvHighlight text={String(v)} highlights={highlightText} />
      </dd>

      {inlineActions && (
        <div className="showInlineAction">
          {inlineActions?.map(action => (
            <React.Fragment key={action.name}>{action(k, v)}</React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

const MemoizedRow = React.memo(Row);

// TODO: Implement horizontal virtualization
type Props = Omit<AvKeyValueProps, 'orientation'>;

export const AvKeyValueV2 = ({ value = {}, highlightText, limit = 20, inlineActions }: Props) => {
  const parentRef = React.useRef(null);

  const objectKeys = React.useMemo(() => Object.keys(value), [value]);
  const values = limit === -1 ? objectKeys : objectKeys.slice(0, limit);

  const rowVirtualizer = useVirtualizer({
    count: values.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => MIN_ROW_HEIGHT,
    measureElement(element, entry) {
      return entry?.contentRect.height ?? element.getBoundingClientRect().height;
    },
  });

  const items = rowVirtualizer.getVirtualItems();

  return (
    <Box
      ref={parentRef}
      sx={{
        '--total-size': rowVirtualizer.getTotalSize() + 'px',
        '--translate-y': `translateY(${items.at(0)?.start ?? 0}px)`,
        overflowY: 'auto',
        height: '100%',
        contain: 'strict',
        overflowAnchor: 'none',
      }}>
      <StyledContainer>
        <dl>
          {items.map(virtualRow => {
            const key = values[virtualRow.index];
            const v = value[key];
            return (
              <MemoizedRow
                key={virtualRow.key}
                k={key}
                v={v}
                highlightText={highlightText}
                inlineActions={inlineActions}
                data-index={virtualRow.index}
                ref={rowVirtualizer.measureElement}
              />
            );
          })}
        </dl>
      </StyledContainer>
    </Box>
  );
};
